import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { copiarConfigPdv } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

export default function CopiarConfigPdvDialog({
  open,
  onClose,
  pdvs = [],
  origemIdInicial = '',
  onSuccess,
}) {
  const [origemId, setOrigemId] = useState('');
  const [destinoIds, setDestinoIds] = useState([]);
  const [incluirLogos, setIncluirLogos] = useState(true);
  const [incluirImpressoras, setIncluirImpressoras] = useState(true);
  const [incluirBalancaPasta, setIncluirBalancaPasta] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const opcoesOrigem = useMemo(
    () => pdvs.map((p) => ({
      id: p.id,
      label: `${p.descricao || `PDV ${p.id}`} (${p.codigoInstalacao || '—'})`,
    })),
    [pdvs],
  );

  const opcoesDestino = useMemo(
    () => opcoesOrigem.filter((p) => String(p.id) !== String(origemId)),
    [opcoesOrigem, origemId],
  );

  useEffect(() => {
    if (!open) return;
    setError('');
    setDestinoIds([]);
    setIncluirLogos(true);
    setIncluirImpressoras(true);
    setIncluirBalancaPasta(false);
    const inicial = origemIdInicial ? String(origemIdInicial) : '';
    setOrigemId(inicial || (opcoesOrigem[0] ? String(opcoesOrigem[0].id) : ''));
  }, [open, origemIdInicial, opcoesOrigem]);

  const toggleDestino = (id) => {
    setDestinoIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  };

  const copiar = async () => {
    setError('');
    const origem = Number(origemId);
    if (!origem) {
      setError('Selecione o PDV de origem.');
      return;
    }
    if (destinoIds.length === 0) {
      setError('Marque ao menos um PDV destino.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await copiarConfigPdv(origem, {
        destinoIds,
        incluirLogos,
        incluirImpressoras,
        incluirBalancaPasta,
      });
      const qtd = data?.atualizados ?? destinoIds.length;
      onSuccess?.(`Configuração copiada para ${qtd} PDV(s).`);
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível copiar a configuração.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copiar configuração entre PDVs</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Replica modo de operação, fiscal, impressão, TEF, balança e demais parâmetros.
          Cada destino mantém descrição, código de instalação e filial.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="copiar-pdv-origem-label">PDV origem</InputLabel>
          <Select
            labelId="copiar-pdv-origem-label"
            label="PDV origem"
            value={origemId}
            onChange={(e) => {
              setOrigemId(e.target.value);
              setDestinoIds([]);
            }}
          >
            {opcoesOrigem.map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>{p.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>PDVs destino</Typography>
        {opcoesDestino.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Não há outros PDVs para receber a configuração.
          </Typography>
        ) : (
          <FormGroup sx={{ mb: 2, maxHeight: 180, overflow: 'auto' }}>
            {opcoesDestino.map((p) => (
              <FormControlLabel
                key={p.id}
                control={(
                  <Checkbox
                    checked={destinoIds.includes(p.id)}
                    onChange={() => toggleDestino(p.id)}
                  />
                )}
                label={p.label}
              />
            ))}
          </FormGroup>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Incluir também</Typography>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={incluirLogos} onChange={(e) => setIncluirLogos(e.target.checked)} />}
            label="Logos (sistema e impressos)"
          />
          <FormControlLabel
            control={<Checkbox checked={incluirImpressoras} onChange={(e) => setIncluirImpressoras(e.target.checked)} />}
            label="Nomes das impressoras (cupom, DANFE, etiquetas)"
          />
          <FormControlLabel
            control={<Checkbox checked={incluirBalancaPasta} onChange={(e) => setIncluirBalancaPasta(e.target.checked)} />}
            label="Pasta da carga de balança"
          />
        </FormGroup>
        <FormHelperText sx={{ mt: 1 }}>
          Por padrão, a pasta da balança não é copiada — costuma ser específica de cada máquina.
        </FormHelperText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={saving}>Cancelar</Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <ContentCopyIcon />}
          onClick={copiar}
          disabled={saving || opcoesDestino.length === 0}
        >
          Copiar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
